import { AccountType, AccountCategory } from '../types/accounting';
import { BaseService } from './BaseService';
import { ValidationError } from '../utils/errors';
import { PoolClient, QueryResult } from 'pg';

interface Account {
  id: string;
  fiscal_sponsor_id: string;
  code: string;
  name: string;
  type: AccountType;
  category: AccountCategory;
  subcategory?: string;
  isContra?: boolean;
  parent_id?: string;
  description?: string;
  created_at: Date;
  updated_at: Date;
}

interface AccountHierarchyRow {
  id: string;
  code: string;
  name: string;
  type: AccountType;
  category: AccountCategory;
  description?: string;
  parent_id?: string;
  level: number;
  path: string[];
}

interface CreateAccountDTO {
  code: string;
  name: string;
  type: AccountType;
  category: AccountCategory;
  subcategory?: string;
  isContra?: boolean;
  parent_id?: string;
  description?: string;
}

export class AccountService extends BaseService {
  async executeQueryWithClient<T>(
    callback: (client: PoolClient) => Promise<T>
  ): Promise<T> {
    if (!this.pool) {
      throw new Error('Database pool is not initialized');
    }
    
    const client = await this.pool.connect();
    try {
      return await callback(client);
    } finally {
      client.release();
    }
  }

  async createAccount(
    fiscalSponsorId: string,
    accountData: Omit<Account, 'id' | 'fiscal_sponsor_id' | 'created_at' | 'updated_at'>
  ): Promise<Account> {
    if (!accountData.code) {
      throw new ValidationError('Account code is required');
    }
    
    this.validateRequired(accountData, ['code', 'name', 'type', 'category']);
    await this.validateAccountCode(fiscalSponsorId, accountData.code);

    return await this.executeQueryWithClient(async (client: PoolClient) => {
      const result = await client.query<Account>(
        `INSERT INTO sponsfi.accounts (
          fiscal_sponsor_id, code, name, type, category, 
          description, parent_id
        ) VALUES ($1, $2, $3, $4, $5, $6, $7)
        RETURNING *`,
        [
          fiscalSponsorId,
          accountData.code,
          accountData.name,
          accountData.type,
          accountData.category,
          accountData.description || null,
          accountData.parent_id || null
        ]
      );

      const newAccount = result.rows[0];
      await this.createAccountAuditTrail(client, 'CREATE', newAccount);
      return newAccount;
    });
  }

  async getAccountHierarchy(fiscalSponsorId: string): Promise<AccountHierarchyRow[]> {
    return await this.executeQueryWithClient(async (client: PoolClient) => {
      const result = await client.query<AccountHierarchyRow>(
        `WITH RECURSIVE account_tree AS (
          -- Base case: get all root accounts
          SELECT 
            id, code, name, type, category, description, parent_id,
            0 as level,
            ARRAY[code] as path
          FROM sponsfi.accounts
          WHERE fiscal_sponsor_id = $1 AND parent_id IS NULL
          
          UNION ALL
          
          -- Recursive case: get child accounts
          SELECT 
            a.id, a.code, a.name, a.type, a.category, a.description, a.parent_id,
            at.level + 1,
            at.path || a.code
          FROM sponsfi.accounts a
          JOIN account_tree at ON a.parent_id = at.id
          WHERE a.fiscal_sponsor_id = $1
        )
        SELECT * FROM account_tree
        ORDER BY path`,
        [fiscalSponsorId]
      );
      
      return result.rows;
    });
  }

  async getAccountBalance(
    fiscalSponsorId: string,
    accountId: string,
    includeSubAccounts: boolean = true
  ): Promise<number> {
    return await this.executeQueryWithClient(async (client: PoolClient) => {
      const result = await client.query<{ balance: string }>(
        `SELECT COALESCE(SUM(
          CASE 
            WHEN je.debit > 0 THEN je.debit
            WHEN je.credit > 0 THEN -je.credit
            ELSE 0
          END
        ), 0) as balance
        FROM sponsfi.journal_entries je
        JOIN sponsfi.journals j ON je.journal_id = j.id
        WHERE j.fiscal_sponsor_id = $1
        AND je.account_id ${includeSubAccounts ? 'IN (' : '= $2'}
          ${includeSubAccounts ? `
            WITH RECURSIVE account_tree AS (
              -- Base case: the specified account
              SELECT id FROM sponsfi.accounts
              WHERE fiscal_sponsor_id = $1 AND id = $2
              
              UNION ALL
              
              -- Recursive case: child accounts
              SELECT a.id
              FROM sponsfi.accounts a
              JOIN account_tree at ON a.parent_id = at.id
              WHERE a.fiscal_sponsor_id = $1
            )
            SELECT id FROM account_tree
          )` : ''}`,
        [fiscalSponsorId, accountId]
      );
      
      // Convert string balance to number
      return parseFloat(result.rows[0]?.balance || '0');
    });
  }

  private async createAccountAuditTrail(
    client: PoolClient,
    action: string,
    account: Account
  ): Promise<void> {
    await client.query(
      `INSERT INTO sponsfi.audit_log (
        fiscal_sponsor_id, entity_type, entity_id, 
        action, changes, performed_by
      ) VALUES ($1, 'account', $2, $3, $4, $5)`,
      [
        account.fiscal_sponsor_id,
        account.id,
        action,
        { account_data: account },
        'system' // TODO: Replace with actual user ID from context
      ]
    );
  }

  async validateAccountCode(
    fiscalSponsorId: string,
    code: string,
    excludeAccountId?: string
  ): Promise<boolean> {
    if (!code) {
      throw new Error('Account code is required');
    }
    
    if (!/^\d{3,4}(\.\d{2,3})?$/.test(code)) {
      throw new Error(
        'Invalid account code format. Must be XXX or XXX.XX or XXXX.XXX'
      );
    }

    const result = await this.executeQueryWithClient(async (client: PoolClient) => {
      const existsResult = await client.query<{ exists: boolean }>(
        `SELECT EXISTS(
          SELECT 1 FROM sponsfi.accounts 
          WHERE fiscal_sponsor_id = $1 
          AND code = $2
          ${excludeAccountId ? 'AND id != $3' : ''}
        ) as exists`,
        excludeAccountId 
          ? [fiscalSponsorId, code, excludeAccountId]
          : [fiscalSponsorId, code]
      );
      
      return existsResult.rows[0].exists;
    });
    
    if (result) {
      throw new ValidationError(`Account code "${code}" is already in use`);
    }
    
    return true;
  }

  async getAllAccounts(fiscalSponsorId: string): Promise<Account[]> {
    return await this.executeQueryWithClient(async (client: PoolClient) => {
      const result = await client.query<Account>(
        `SELECT * FROM sponsfi.accounts 
        WHERE fiscal_sponsor_id = $1
        ORDER BY code ASC`,
        [fiscalSponsorId]
      );
      
      return result.rows;
    });
  }
} 